// src/features/form/SettingsTab.tsx
// FormSettings — label-left settings rows (docs/ux/14-forms.md surface 3),
// autosave per toggle, one Save for the grouped text fields.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Alert, Button, Input, Textarea } from '@react/components';
import { FormRow } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { formApi } from '@core/api';
import type { Form, FormSettings } from '@core/types/form';
import { errMessage, parseClearableInt } from './helpers';

export interface SettingsTabProps {
  form: Form;
  onFormChanged: (form: Form) => void;
}

const DEFAULT_SETTINGS: FormSettings = {
  allow_anonymous: false,
  allow_multi_submit: false,
  requires_auth: true,
  collect_location: false,
  show_progress_bar: true,
  auto_save: false,
  max_submissions: 0,
  confirmation_message: '',
  redirect_url: '',
  notify_on_submit: false,
};

export function SettingsTab({ form, onFormChanged }: SettingsTabProps) {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const settings = form.settings ?? DEFAULT_SETTINGS;
  const [confirmationMessage, setConfirmationMessage] = useState(settings.confirmation_message);
  const [redirectUrl, setRedirectUrl] = useState(settings.redirect_url);
  const [maxSubmissions, setMaxSubmissions] = useState(settings.max_submissions ? String(settings.max_submissions) : '');
  const dirty = confirmationMessage !== settings.confirmation_message || redirectUrl !== settings.redirect_url || maxSubmissions !== (settings.max_submissions ? String(settings.max_submissions) : '');

  const save = async (patch: Partial<FormSettings>) => {
    if (!form.entity?.id) return;
    setBusy(true);
    setError(null);
    try {
      const next = await formApi.UpdateForm(form.entity.id, {
        form: { ...form, settings: { ...settings, ...patch } },
      });
      if (next.form) onFormChanged(next.form);
    } catch (e) {
      setError(errMessage(e, t('form.settings.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const saveTextFields = () =>
    save({
      confirmation_message: confirmationMessage,
      redirect_url: redirectUrl,
      max_submissions: parseClearableInt(maxSubmissions) ?? 0,
    });

  return (
    <div className="max-w-xl space-y-1">
      {error && <Alert variant="error">{error}</Alert>}

      <FormRow label={t('form.settings.allowAnonymous')} hint={t('form.settings.allowAnonymousHint')}>
        <ToggleSwitch label="" checked={settings.allow_anonymous} onChange={(v) => save({ allow_anonymous: v })} disabled={busy} />
      </FormRow>
      <FormRow label={t('form.settings.requiresAuth')}>
        <ToggleSwitch label="" checked={settings.requires_auth} onChange={(v) => save({ requires_auth: v })} disabled={busy} />
      </FormRow>
      <FormRow label={t('form.settings.allowMultiSubmit')}>
        <ToggleSwitch label="" checked={settings.allow_multi_submit} onChange={(v) => save({ allow_multi_submit: v })} disabled={busy} />
      </FormRow>
      <FormRow label={t('form.settings.showProgressBar')}>
        <ToggleSwitch label="" checked={settings.show_progress_bar} onChange={(v) => save({ show_progress_bar: v })} disabled={busy} />
      </FormRow>
      <FormRow label={t('form.settings.autoSave')}>
        <ToggleSwitch label="" checked={settings.auto_save} onChange={(v) => save({ auto_save: v })} disabled={busy} />
      </FormRow>
      <FormRow label={t('form.settings.collectLocation')}>
        <ToggleSwitch label="" checked={settings.collect_location} onChange={(v) => save({ collect_location: v })} disabled={busy} />
      </FormRow>
      <FormRow label={t('form.settings.notifyOnSubmit')}>
        <ToggleSwitch label="" checked={settings.notify_on_submit} onChange={(v) => save({ notify_on_submit: v })} disabled={busy} />
      </FormRow>

      <FormRow label={t('form.settings.maxSubmissions')} hint={t('form.settings.maxSubmissionsHint')}>
        <input
          type="text"
          inputMode="numeric"
          value={maxSubmissions}
          onChange={(e) => setMaxSubmissions(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder={t('form.settings.unlimited')}
          className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
        />
      </FormRow>
      <FormRow label={t('form.settings.confirmationMessage')}>
        <Textarea value={confirmationMessage} onChange={(e) => setConfirmationMessage(e.target.value)} rows={2} />
      </FormRow>
      <FormRow label={t('form.settings.redirectUrl')}>
        <Input value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)} placeholder="https://…" />
      </FormRow>

      <div className="flex justify-end pt-2">
        <Button size="sm" onClick={saveTextFields} disabled={!dirty} isLoading={busy}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
